import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, ClipboardText, Package, CurrencyCircleDollar, LinkBreak,
  ArrowsClockwise, Warning, CheckCircle, XCircle, MinusCircle, Minus,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fmtCur = (v) => `Rs ${(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;
const fmtQty = (v) => (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 });

const TABS = [
  { key: 'indent', label: 'Indent Tracking', icon: ClipboardText },
  { key: 'grn', label: 'GRN Reconciliation', icon: Package },
  { key: 'payment', label: 'Payment Reconciliation', icon: CurrencyCircleDollar },
  { key: 'unmapped', label: 'Unmapped Materials', icon: LinkBreak },
];

const ProcurementReconciliation = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'indent');
  const [projects, setProjects] = useState([]);
  const [filterProject, setFilterProject] = useState(searchParams.get('project') || '');

  useEffect(() => {
    axios.get(`${BACKEND}/api/projects?page=1&limit=200`, { withCredentials: true })
      .then(r => setProjects(r.data.projects || r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setSearchParams({ tab: activeTab, ...(filterProject ? { project: filterProject } : {}) });
  }, [activeTab, filterProject, setSearchParams]);

  const confirmedProjects = projects.filter(p => p.status === 'Confirmed');

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="border-zinc-300 rounded-none hover:bg-zinc-100 mb-3" data-testid="recon-back-btn">
            <ArrowLeft size={20} className="mr-2" /> Back
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Procurement Reconciliation</h1>
          <p className="text-sm text-zinc-600 mt-1">Indent → PO → GRN → Payment control and tracking</p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Project Filter */}
        <div className="flex items-center gap-3 mb-5">
          <select value={filterProject} onChange={(e) => setFilterProject(e.target.value)}
            className="bg-white border border-zinc-200 rounded-none p-2.5 text-sm min-w-[260px]" data-testid="recon-project-filter">
            <option value="">Select Project</option>
            {confirmedProjects.map(p => <option key={p.id} value={p.id}>{p.pi_number} — {p.client_name}</option>)}
          </select>
          {!filterProject && <p className="text-xs text-amber-600">Select a project to view reconciliation data</p>}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-200 mb-6 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${activeTab === t.key ? 'border-blue-700 text-blue-700' : 'border-transparent text-zinc-500 hover:text-zinc-700'}`}
              data-testid={`recon-tab-${t.key}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>

        {!filterProject ? (
          <EmptyState message="Select a project above to view reconciliation data" />
        ) : (
          <>
            {activeTab === 'indent' && <IndentTrackingTab projectId={filterProject} />}
            {activeTab === 'grn' && <GRNReconciliationTab projectId={filterProject} />}
            {activeTab === 'payment' && <PaymentReconciliationTab projectId={filterProject} />}
            {activeTab === 'unmapped' && <UnmappedMaterialsTab projectId={filterProject} />}
          </>
        )}
      </main>
    </div>
  );
};

const EmptyState = ({ message }) => (
  <div className="bg-white border border-zinc-200 p-12 text-center" data-testid="recon-empty">
    <Package size={48} className="mx-auto text-zinc-300 mb-3" />
    <p className="text-sm text-zinc-500">{message}</p>
  </div>
);

const StatusBadge = ({ status, map }) => {
  const style = map[status] || { bg: 'bg-zinc-100', text: 'text-zinc-600' };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 ${style.bg} ${style.text}`} data-testid="status-badge">{status}</span>;
};

const INDENT_STATUS_MAP = {
  'Fully Ordered': { bg: 'bg-green-100', text: 'text-green-700' },
  'Partially Ordered': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Not Ordered': { bg: 'bg-red-100', text: 'text-red-700' },
};
const GRN_STATUS_MAP = {
  'Fully Received': { bg: 'bg-green-100', text: 'text-green-700' },
  'Short Supply': { bg: 'bg-red-100', text: 'text-red-700' },
  'Excess Received': { bg: 'bg-amber-100', text: 'text-amber-700' },
  'Pending': { bg: 'bg-zinc-100', text: 'text-zinc-600' },
};

/* ========== 1. INDENT TRACKING ========== */
const IndentTrackingTab = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${BACKEND}/api/projects/${projectId}/indent-tracking`, { withCredentials: true }); setRows(data); }
    catch { toast.error('Failed to load indent tracking'); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;
  if (rows.length === 0) return <EmptyState message="No indents found for this project" />;

  const summary = {
    total: rows.length,
    fullyOrdered: rows.filter(r => r.order_status === 'Fully Ordered').length,
    partial: rows.filter(r => r.order_status === 'Partially Ordered').length,
    notOrdered: rows.filter(r => r.order_status === 'Not Ordered').length,
  };

  return (
    <div data-testid="indent-tracking-view">
      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <SummaryCard label="Total Indents" value={summary.total} />
        <SummaryCard label="Fully Ordered" value={summary.fullyOrdered} color="text-green-700" bg="bg-green-50" />
        <SummaryCard label="Partially Ordered" value={summary.partial} color="text-amber-700" bg="bg-amber-50" />
        <SummaryCard label="Not Ordered" value={summary.notOrdered} color="text-red-700" bg="bg-red-50" />
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="indent-tracking-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {['Indent #', 'Description', 'Unit', 'Required', 'Ordered', 'Balance', 'Status', 'Priority', 'Req. Date'].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`indent-row-${r.id}`}>
                <td className="px-3 py-2.5 text-xs font-mono text-zinc-600">{r.indent_number}</td>
                <td className="px-3 py-2.5 text-xs font-medium text-zinc-800 max-w-[200px] truncate">{r.description}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{r.unit}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{fmtQty(r.required_quantity)}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-blue-700 text-right">{fmtQty(r.ordered_quantity)}</td>
                <td className={`px-3 py-2.5 text-xs font-bold text-right ${r.balance_quantity > 0 ? 'text-red-600' : 'text-green-600'}`}>{fmtQty(r.balance_quantity)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={r.order_status} map={INDENT_STATUS_MAP} /></td>
                <td className="px-3 py-2.5"><PriorityBadge priority={r.priority} /></td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{r.required_date ? new Date(r.required_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ========== 2. GRN RECONCILIATION ========== */
const GRNReconciliationTab = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${BACKEND}/api/projects/${projectId}/grn-reconciliation`, { withCredentials: true }); setRows(data); }
    catch { toast.error('Failed to load GRN reconciliation'); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;
  if (rows.length === 0) return <EmptyState message="No PO line items found for reconciliation" />;

  const summary = {
    total: rows.length,
    fullyReceived: rows.filter(r => r.status === 'Fully Received').length,
    shortSupply: rows.filter(r => r.status === 'Short Supply').length,
    excess: rows.filter(r => r.status === 'Excess Received').length,
    pending: rows.filter(r => r.status === 'Pending').length,
  };

  return (
    <div data-testid="grn-recon-view">
      <div className="grid grid-cols-5 gap-3 mb-4">
        <SummaryCard label="Total PO Items" value={summary.total} />
        <SummaryCard label="Fully Received" value={summary.fullyReceived} color="text-green-700" bg="bg-green-50" />
        <SummaryCard label="Short Supply" value={summary.shortSupply} color="text-red-700" bg="bg-red-50" />
        <SummaryCard label="Excess" value={summary.excess} color="text-amber-700" bg="bg-amber-50" />
        <SummaryCard label="Pending" value={summary.pending} color="text-zinc-600" bg="bg-zinc-50" />
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="grn-recon-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {['PO #', 'Supplier', 'Description', 'Unit', 'Ordered', 'Received', 'Accepted', 'Rejected', 'Shortfall', 'Excess', 'Status'].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`grn-row-${i}`}>
                <td className="px-3 py-2.5 text-xs font-mono text-zinc-600">{r.po_number}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-600 max-w-[120px] truncate">{r.supplier_name}</td>
                <td className="px-3 py-2.5 text-xs font-medium text-zinc-800 max-w-[180px] truncate">{r.description}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{r.unit}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{fmtQty(r.ordered_qty)}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-blue-700 text-right">{fmtQty(r.received_qty)}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-green-700 text-right">{fmtQty(r.accepted_qty)}</td>
                <td className={`px-3 py-2.5 text-xs font-bold text-right ${r.rejected_qty > 0 ? 'text-red-600' : 'text-zinc-400'}`}>{fmtQty(r.rejected_qty)}</td>
                <td className={`px-3 py-2.5 text-xs font-bold text-right ${r.shortfall > 0 ? 'text-red-600' : 'text-zinc-400'}`}>{fmtQty(r.shortfall)}</td>
                <td className={`px-3 py-2.5 text-xs font-bold text-right ${r.excess > 0 ? 'text-amber-600' : 'text-zinc-400'}`}>{fmtQty(r.excess)}</td>
                <td className="px-3 py-2.5"><StatusBadge status={r.status} map={GRN_STATUS_MAP} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ========== 3. PAYMENT RECONCILIATION ========== */
const PaymentReconciliationTab = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try { const { data } = await axios.get(`${BACKEND}/api/projects/${projectId}/payment-reconciliation`, { withCredentials: true }); setRows(data); }
    catch { toast.error('Failed to load payment reconciliation'); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { fetch(); }, [fetch]);

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;
  if (rows.length === 0) return <EmptyState message="No purchase orders found for payment reconciliation" />;

  const totalOrdered = rows.reduce((s, r) => s + r.ordered_value, 0);
  const totalGRN = rows.reduce((s, r) => s + r.grn_accepted_value, 0);
  const totalPaid = rows.reduce((s, r) => s + r.amount_paid, 0);
  const totalOverpayment = rows.reduce((s, r) => s + r.overpayment, 0);
  const alertCount = rows.filter(r => r.alerts.length > 0).length;

  return (
    <div data-testid="payment-recon-view">
      <div className="grid grid-cols-5 gap-3 mb-4">
        <SummaryCard label="Ordered Value" value={fmtCur(totalOrdered)} />
        <SummaryCard label="GRN Value" value={fmtCur(totalGRN)} color="text-blue-700" bg="bg-blue-50" />
        <SummaryCard label="Paid" value={fmtCur(totalPaid)} color="text-green-700" bg="bg-green-50" />
        <SummaryCard label="Overpayment" value={fmtCur(totalOverpayment)} color={totalOverpayment > 0 ? 'text-red-700' : 'text-green-700'} bg={totalOverpayment > 0 ? 'bg-red-50' : 'bg-green-50'} />
        <SummaryCard label="Alerts" value={alertCount} color={alertCount > 0 ? 'text-red-700' : 'text-green-700'} bg={alertCount > 0 ? 'bg-red-50' : 'bg-green-50'} />
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="payment-recon-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {['PO #', 'Supplier', 'Ordered', 'GRN Value', 'Paid', 'Balance', 'Overpayment', 'Alerts'].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className={`border-b border-zinc-100 hover:bg-zinc-50 ${r.alerts.length > 0 ? 'bg-red-50/30' : ''}`} data-testid={`pay-row-${i}`}>
                <td className="px-3 py-2.5 text-xs font-mono text-zinc-600">{r.po_number}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-600 max-w-[140px] truncate">{r.supplier_name}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{fmtCur(r.ordered_value)}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-blue-700 text-right">{fmtCur(r.grn_accepted_value)}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-green-700 text-right">{fmtCur(r.amount_paid)}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{fmtCur(r.balance_payable)}</td>
                <td className={`px-3 py-2.5 text-xs font-bold text-right ${r.overpayment > 0 ? 'text-red-600' : 'text-zinc-400'}`}>{fmtCur(r.overpayment)}</td>
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1">
                    {r.alerts.includes('overpayment') && <AlertTag label="Overpayment" color="red" />}
                    {r.alerts.includes('payment_without_grn') && <AlertTag label="Payment w/o GRN" color="amber" />}
                    {r.alerts.includes('grn_without_payment') && <AlertTag label="GRN w/o Payment" color="amber" />}
                    {r.alerts.length === 0 && <span className="text-[10px] text-green-600 font-medium">OK</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ========== 4. UNMAPPED MATERIALS ========== */
const UnmappedMaterialsTab = ({ projectId }) => {
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mapDialog, setMapDialog] = useState(false);
  const [mapItem, setMapItem] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [unmRes, matRes] = await Promise.all([
        axios.get(`${BACKEND}/api/projects/${projectId}/unmapped-materials`, { withCredentials: true }),
        axios.get(`${BACKEND}/api/material-master`, { withCredentials: true }),
      ]);
      setRows(unmRes.data);
      setMaterials(matRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const handleMap = async () => {
    if (!selectedMaterial || !mapItem) return;
    try {
      await axios.post(`${BACKEND}/api/grn/items/${mapItem.grn_id}/map-material`, {
        material_id: selectedMaterial, description: mapItem.description,
      }, { withCredentials: true });
      toast.success('Material mapped');
      setMapDialog(false); setMapItem(null); setSelectedMaterial('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to map'); }
  };

  if (loading) return <div className="text-center py-8 text-zinc-500">Loading...</div>;
  if (rows.length === 0) return (
    <div className="bg-green-50 border border-green-200 p-8 text-center" data-testid="all-mapped">
      <CheckCircle size={40} className="mx-auto text-green-500 mb-2" />
      <p className="text-sm font-medium text-green-700">All materials are mapped</p>
      <p className="text-xs text-green-600 mt-1">No unmapped GRN items for this project</p>
    </div>
  );

  return (
    <div data-testid="unmapped-materials-view">
      <div className="flex items-center gap-2 mb-4">
        <Warning size={16} className="text-amber-500" />
        <span className="text-xs text-zinc-600">{rows.length} material(s) received via GRN but not linked to Material Master. Map them for accurate reporting.</span>
      </div>

      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm" data-testid="unmapped-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr>
              {['GRN #', 'Supplier', 'Item Description', 'Qty', 'Unit', 'Delivery Date', 'Action'].map(h => (
                <th key={h} className="px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-wider text-zinc-500 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`unmapped-row-${i}`}>
                <td className="px-3 py-2.5 text-xs font-mono text-zinc-600">{r.grn_number}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-600">{r.supplier_name}</td>
                <td className="px-3 py-2.5 text-xs font-medium text-zinc-800">{r.description}</td>
                <td className="px-3 py-2.5 text-xs font-bold text-zinc-800 text-right">{fmtQty(r.quantity)}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{r.unit}</td>
                <td className="px-3 py-2.5 text-xs text-zinc-500">{r.delivery_date ? new Date(r.delivery_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-'}</td>
                <td className="px-3 py-2.5">
                  <Button size="sm" variant="outline" className="rounded-none border-blue-300 text-blue-700 text-xs h-7"
                    onClick={() => { setMapItem(r); setSelectedMaterial(''); setMapDialog(true); }} data-testid={`map-btn-${i}`}>
                    Map
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Map Dialog */}
      <Dialog open={mapDialog} onOpenChange={setMapDialog}>
        <DialogContent className="bg-white border border-zinc-200 rounded-none shadow-xl max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-bold">Map Material</DialogTitle></DialogHeader>
          {mapItem && (
            <div className="space-y-4 mt-2">
              <div className="bg-zinc-50 border border-zinc-200 p-3">
                <p className="text-xs text-zinc-500">GRN Item</p>
                <p className="text-sm font-semibold text-zinc-800">{mapItem.description}</p>
                <p className="text-xs text-zinc-400 mt-1">{mapItem.grn_number} — {fmtQty(mapItem.quantity)} {mapItem.unit}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-500 mb-1">Select Material Master Entry</p>
                <select value={selectedMaterial} onChange={(e) => setSelectedMaterial(e.target.value)}
                  className="w-full bg-zinc-50 border border-zinc-200 rounded-none p-2.5 text-sm" data-testid="map-material-select">
                  <option value="">Select Material</option>
                  {materials.map(m => <option key={m.id} value={m.id}>{m.material_code} — {m.description} ({m.unit})</option>)}
                </select>
              </div>
              <Button onClick={handleMap} disabled={!selectedMaterial} className="w-full bg-blue-700 hover:bg-blue-800 text-white rounded-none" data-testid="map-confirm-btn">
                Confirm Mapping
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ========== SHARED COMPONENTS ========== */
const SummaryCard = ({ label, value, color = 'text-zinc-900', bg = 'bg-white' }) => (
  <div className={`${bg} border border-zinc-200 px-3 py-2.5`}>
    <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{label}</p>
    <p className={`text-lg font-bold ${color}`}>{value}</p>
  </div>
);

const PriorityBadge = ({ priority }) => {
  const map = {
    critical: 'bg-red-100 text-red-700',
    high: 'bg-orange-100 text-orange-700',
    normal: 'bg-blue-100 text-blue-700',
    low: 'bg-zinc-100 text-zinc-600',
  };
  return <span className={`text-[10px] font-semibold px-2 py-0.5 ${map[priority] || map.normal}`}>{priority}</span>;
};

const AlertTag = ({ label, color }) => {
  const colors = {
    red: 'bg-red-100 text-red-700 border-red-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
  };
  return <span className={`text-[10px] font-semibold px-1.5 py-0.5 border ${colors[color] || colors.amber}`}>{label}</span>;
};

export default ProcurementReconciliation;
